import React, { useState } from 'react';
import { company } from '../data/companyData';
import './ContactPage.css';

export default function ContactPage() {
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    serviceRequired: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const encode = (data) => {
    return Object.keys(data)
      .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
      .join("&");
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "contact", ...form }),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit enquiry (Status ${response.status}). Please try again.`);
      }

      setSubmitted(true);
      setForm({
        fullName: '',
        email: '',
        phone: '',
        serviceRequired: '',
        message: ''
      });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="contact-page">

      <section className="page-header">
        <div className="container page-header__inner">
          <span className="tag">Get In Touch</span>
          <h1 className="page-header__title">
            Let's Discuss<br />
            <span style={{ color: 'var(--gold)' }}>Your Project</span>
          </h1>
          <p className="page-header__sub">
            Ready to explore a partnership? Our team responds within one business day.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container contact-layout">

          {/* Contact info */}
          <div className="contact-info">
            <h2 className="contact-info__title">Contact Information</h2>
            <p className="contact-info__desc">
              Reach us via phone, email, or submit the enquiry form and we'll get back
              to you promptly. On-call services are available 24/7.
            </p>

            <div className="contact-details">
              {[
                { icon: '📍', label: 'Location', value: company.address },
                { icon: '📞', label: 'Phone', value: company.phone },
                { icon: '✉️', label: 'Email', value: company.email },
                { icon: '🌐', label: 'Website', value: company.website },
              ].map(d => (
                <div key={d.label} className="contact-detail">
                  <div className="contact-detail__icon">{d.icon}</div>
                  <div>
                    <div className="contact-detail__label">{d.label}</div>
                    <div className="contact-detail__value">
                      {d.label === 'Phone' ? (
                        <a href={`tel:${d.value.replace(/\s+/g, '')}`}>{d.value}</a>
                      ) : d.label === 'Email' ? (
                        <a href={`mailto:${d.value}`}>{d.value}</a>
                      ) : d.label === 'Website' ? (
                        <a href={`https://${d.value}`} target="_blank" rel="noopener noreferrer">{d.value}</a>
                      ) : (
                        d.value
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="contact-info__box">
              <div className="contact-info__box-icon">⚡</div>
              <div>
                <div className="contact-info__box-title">24/7 Emergency Response</div>
                <div className="contact-info__box-text">
                  For urgent maintenance issues, call us directly. Emergency attendance
                  is included in all service contracts at no additional cost.
                </div>
              </div>
            </div>

            <div className="contact-info__established">
              <div className="contact-info__est-label">Established</div>
              <div className="contact-info__est-val">{company.established}</div>
            </div>
          </div>

          {/* Form */}
          <div className="contact-form-wrap">
            {!submitted ? (
              <form className="contact-form" name="contact" method="POST" data-netlify="true" data-netlify-honeypot="bot-field" onSubmit={handleSubmit}>
                <input type="hidden" name="form-name" value="contact" />
                <h3 className="contact-form__title">Send an Enquiry</h3>

                {error && (
                  <div className="contact-error">
                    <span>⚠️</span>
                    <span>{error}</span>
                  </div>
                )}

                <div className="contact-form__row">
                  <div className="form-group">
                    <label className="form-label">Full Name *</label>
                    <input
                      className="form-input"
                      type="text"
                      name="fullName"
                      value={form.fullName}
                      onChange={handleChange}
                      placeholder="Your full name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email Address *</label>
                    <input
                      className="form-input"
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="work@primefixsolutions.co.nz"
                      required
                    />
                  </div>
                </div>

                <div className="contact-form__row">
                  <div className="form-group">
                    <label className="form-label">Phone Number</label>
                    <input
                      className="form-input"
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+64 27 298 9193"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Service Required</label>
                    <select
                      className="form-input form-select"
                      name="serviceRequired"
                      value={form.serviceRequired}
                      onChange={handleChange}
                    >
                      <option value="">Select a service</option>
                      <option>MEP Systems Maintenance</option>
                      <option>HVAC & Cooling Systems</option>
                      <option>Civil & Architectural Works</option>
                      <option>Fire Safety Systems</option>
                      <option>Integrated Facility Management</option>
                      <option>Preventive Maintenance Program</option>
                      <option>Other / General Enquiry</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Message *</label>
                  <textarea
                    className="form-input form-textarea"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Please describe your facility, requirements, or any specific concerns..."
                    rows={5}
                    required
                  />
                </div>

                <button type="submit" className="btn btn-primary contact-form__submit" disabled={loading}>
                  {loading ? 'Sending Enquiry...' : 'Send Enquiry →'}
                </button>
              </form>
            ) : (
              <div className="contact-success">
                <div className="contact-success__icon">✓</div>
                <h3>Enquiry Received</h3>
                <p>Thank you for reaching out. Our team will review your enquiry and respond within one business day.</p>
                <button
                  className="btn btn-outline"
                  style={{ marginTop: 24 }}
                  onClick={() => {
                    setSubmitted(false);
                    setError('');
                  }}
                >
                  Send Another Enquiry
                </button>
              </div>
            )}
          </div>

        </div>
      </section>

    </main>
  );
}
